import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  formatPriceDisplay,
  getMatchingTier,
  getQuantityInTier,
} from '@bbkPricingPage/helpers/amount-calculator';
import { getPriceWithDifferentialPrices } from '@bbkPricingPage/lib/price-utils';
import {
  PricingModel,
  type Frequency,
  type Price,
} from '@bbkPricingPage/types/pricing-table-types';

interface PriceSplitProps {
  price: Price;
  selectedPlanId: string;
  frequency: Frequency;
  quantity?: number;
  multiplier?: number;
  strikethrough?: boolean;
}

export const PriceSplit: React.FC<PriceSplitProps> = ({
  price: priceProp,
  selectedPlanId,
  frequency,
  quantity = 1,
  multiplier,
  strikethrough = false,
}) => {
  const price = getPriceWithDifferentialPrices(priceProp, selectedPlanId);
  const isMultiDecimalPrice = price.tiers?.some(
    (tier) => tier.starting_unit === 0
  );

  const renderLine = (amount: number, count: number, key?: string | number) => (
    <div
      key={key}
      className={cs(
        'tw-text-sm tw-text-[var(--pricing-page-text-font-text-color)] tw-text-opacity-50',
        strikethrough && 'tw-line-through'
      )}
    >
      {formatPriceDisplay(amount, price.currency, frequency, multiplier)}
      {price.pricing_model === PricingModel.stairstep
        ? ` for ${count} ${count === 1 ? 'unit' : 'units'}`
        : ` × ${count}`}
    </div>
  );

  const renderAllTiers = () => (
    <div className="tw-space-y-0">
      {price.tiers?.map((tier, idx) => {
        if (quantity < tier.starting_unit) return null;
        const qtyInTier = getQuantityInTier(
          tier,
          quantity,
          isMultiDecimalPrice
        );
        return qtyInTier > 0 ? renderLine(tier.amount, qtyInTier, idx) : null;
      })}
    </div>
  );

  const renderTier = () => {
    const tier = getMatchingTier(price, quantity);
    if (!tier) return null;
    return renderLine(tier.amount, quantity);
  };

  const pricingRenderers: Record<PricingModel, React.ReactNode> = {
    [PricingModel.flat_fee]: null,
    [PricingModel.per_unit]: renderLine(price.amount as number, quantity),
    [PricingModel.tiered]: renderAllTiers(),
    [PricingModel.volume]: renderTier(),
    [PricingModel.stairstep]: renderTier(),
  };

  return pricingRenderers[price.pricing_model];
};
